import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref, shallowRef, watch } from 'vue'
import { isAxiosError } from 'axios'
/* import { useToast } from 'primevue/usetoast' */

import { useCallSttService } from '@/services/call-ai/useCallSttService'
import type { RealtimeTranscriptItem } from '@/services/call-ai/useCallSttService'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'
import {
  AI_DISABLED_MESSAGE,
  /* AI_DISABLED_TOAST_MESSAGE, */
  isAiDisabledError,
} from '@/utils/aiDisabledError'
import {
  createVoiceActivityRecorder,
  type VoiceActivityParty,
  type VoiceActivityRecorder,
  type VoiceActivitySegment,
} from '@/utils/audio/createVoiceActivityRecorder'

type TranscriptStatus = 'pending' | 'transcribing' | 'done' | 'error' | 'skipped'
type CallAiStatus = 'idle' | 'waiting_audio' | 'listening' | 'transcribing' | 'stopped' | 'error'
export type AudioParty = VoiceActivityParty

export interface TranscriptItem {
  id: string
  party: AudioParty
  startedAt: number
  endedAt: number | null
  status: TranscriptStatus
  text: string | null
  errorMessage: string | null
}

export interface DebugAudioChunk {
  id: string
  party: AudioParty
  startedAt: number
  endedAt: number
  durationMs: number
  sizeBytes: number
  status: TranscriptStatus
  text: string | null
  errorMessage: string | null
  wavBlob: Blob
}

const getPartyLabel = (party: AudioParty) => (party === 'agent' ? 'Agente' : 'Cliente')
const TRANSCRIPTION_TIMEOUT_MS = 30000
const SUGGESTION_MIN_NEW_LINES = 3
const SUGGESTION_MIN_CUSTOMER_LINES = 1
const SUGGESTION_COOLDOWN_MS = 20000
const INSIGHT_DEBOUNCE_MS = 2000
const SUGGESTION_CONTEXT_LINES = 12
const SUMMARY_FIRST_MIN_LINES = 8
const SUMMARY_FIRST_MIN_MS = 60000
const SUMMARY_NEW_LINES = 10
const SUMMARY_COOLDOWN_MS = 90000
// Debug STT chunks disabled by default. Set true to re-enable WAV collection/download tooling.
const DEBUG_AUDIO_CHUNKS_ENABLED = false

const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number, message: string) => {
  let timeoutId: number | undefined

  const timeout = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error(message)), timeoutMs)
  })

  try {
    return await Promise.race([promise, timeout])
  } finally {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId)
    }
  }
}

export const useCallAiStore = defineStore('call-ai', () => {
  const workspace = useWorkspaceStore()
  /* const toast = useToast() */
  const localStream = shallowRef<MediaStream | null>(null)
  const remoteStream = shallowRef<MediaStream | null>(null)
  const transcriptItems = ref<TranscriptItem[]>([])
  const debugAudioChunks = ref<DebugAudioChunk[]>([])
  const status = ref<CallAiStatus>('idle')
  const isAutoTranscribing = ref(false)
  const isAiDisabledForCall = ref(false)
  const errorMessage = ref<string | null>(null)
  const suggestions = ref<string[]>([])
  const summary = ref<string | null>(null)
  const isLoadingSuggestions = ref(false)
  const isLoadingSummary = ref(false)
  const suggestionsError = ref<string | null>(null)
  const summaryError = ref<string | null>(null)
  const callStartedAt = ref<number | null>(null)
  const lastSuggestionAt = ref(0)
  const lastSummaryAt = ref(0)
  const lastSuggestedTranscriptIndex = ref(0)
  const lastSummarizedTranscriptIndex = ref(0)
  const lastProvider = ref('')
  const lastModel = ref('')
  const localTrackCount = ref(0)
  const remoteTrackCount = ref(0)
  const activeSpeakers = ref<Record<AudioParty, boolean>>({
    agent: false,
    customer: false,
  })
  const isSuggestionsGenerationEnabled = ref(false)
  const isSummaryGenerationEnabled = ref(false)
  const isRealtimeAiPanelEnabled = ref(false)
  const recorders = new Map<AudioParty, VoiceActivityRecorder>()
  const mediaCleanupHandlers: Array<() => void> = []
  let suggestionsTimer: number | null = null
  let summaryTimer: number | null = null
  let currentCallKey: string | null = null
  let currentUsageSessionId: string | null = null
  let currentUsageGroupId: string | null = null
  let currentUsageGroupRequest: Promise<string> | null = null

  const activeCampaignId = () => workspace.callCampaignId || workspace.selectedCampaignId || ''

  const setSuggestionsGenerationEnabled = (enabled: boolean) => {
    isSuggestionsGenerationEnabled.value = enabled
    if (!enabled) {
      if (suggestionsTimer !== null) {
        window.clearTimeout(suggestionsTimer)
        suggestionsTimer = null
      }
      return
    }
    scheduleRealtimeInsights()
  }

  const setSummaryGenerationEnabled = (enabled: boolean) => {
    isSummaryGenerationEnabled.value = enabled
    if (!enabled) {
      if (summaryTimer !== null) {
        window.clearTimeout(summaryTimer)
        summaryTimer = null
      }
      return
    }
    scheduleRealtimeInsights()
  }

  const setRealtimeAiPanelEnabled = (enabled: boolean) => {
    isRealtimeAiPanelEnabled.value = enabled
    if (!enabled) {
      clearInsightTimers()
      if (isAutoTranscribing.value) {
        void stopAutoTranscription(false).finally(() => {
          if (isRealtimeAiPanelEnabled.value && !isAutoTranscribing.value) {
            void startAutoTranscription()
          }
        })
      }
      return
    }

    if (!isAutoTranscribing.value) {
      void startAutoTranscription()
    }
  }

  const ensureRealtimeUsageGroup = async (): Promise<string> => {
    if (currentUsageGroupId) return currentUsageGroupId
    if (currentUsageGroupRequest) return currentUsageGroupRequest

    const campaignId = activeCampaignId()
    const sessionId = currentUsageSessionId
    if (!campaignId || !sessionId) {
      throw new Error('No hay contexto de llamada para agrupar el uso de IA.')
    }

    const request = (async () => {
      const { createRealtimeCallUsageGroup } = useCallSttService()
      const response = await createRealtimeCallUsageGroup({
        campaign_id: campaignId,
        session_id: sessionId,
      })
      if (currentUsageSessionId === sessionId) {
        currentUsageGroupId = response.usage_group_id
      }
      return response.usage_group_id
    })()
    currentUsageGroupRequest = request

    try {
      return await request
    } finally {
      if (currentUsageGroupRequest === request) {
        currentUsageGroupRequest = null
      }
    }
  }

  const handleAiDisabled = () => {
    const campaignId = activeCampaignId()
    if (campaignId) workspace.setCampaignUseIa(campaignId, false)
    workspace.setUseIa(false)
    if (!isAiDisabledForCall.value) {
      /* toast.add({
        severity: 'warn',
        summary: 'IA deshabilitada',
        detail: AI_DISABLED_TOAST_MESSAGE,
        life: 5000,
      }) */
    }
    isAiDisabledForCall.value = true
    isAutoTranscribing.value = false
    status.value = 'error'
    clearInsightTimers()
    const activeRecorders = [...recorders.values()]
    recorders.clear()
    void Promise.allSettled(activeRecorders.map((recorder) => recorder.stop()))
    suggestions.value = []
    summary.value = null
    suggestionsError.value = AI_DISABLED_MESSAGE
    summaryError.value = AI_DISABLED_MESSAGE
    errorMessage.value = AI_DISABLED_MESSAGE
    transcriptItems.value = transcriptItems.value.map((item) =>
      item.status === 'pending' || item.status === 'transcribing'
        ? { ...item, status: 'skipped', text: null, errorMessage: null }
        : item,
    )
  }

  const countLiveAudioTracks = (stream: MediaStream | null) =>
    stream?.getAudioTracks().filter((track) => track.readyState === 'live').length ?? 0

  const refreshMediaState = () => {
    localTrackCount.value = countLiveAudioTracks(localStream.value)
    remoteTrackCount.value = countLiveAudioTracks(remoteStream.value)
  }

  const addMediaListener = (
    target: MediaStream | MediaStreamTrack,
    eventName: keyof MediaStreamEventMap | keyof MediaStreamTrackEventMap,
    options?: AddEventListenerOptions,
  ) => {
    target.addEventListener(eventName, refreshMediaState as EventListener, options)
    mediaCleanupHandlers.push(() => {
      target.removeEventListener(eventName, refreshMediaState as EventListener)
    })
  }

  const clearMediaListeners = () => {
    while (mediaCleanupHandlers.length > 0) {
      mediaCleanupHandlers.pop()?.()
    }
  }

  const hasLocalAudio = computed(() => localTrackCount.value > 0)
  const hasRemoteAudio = computed(() => remoteTrackCount.value > 0)
  const pendingChunks = computed(
    () =>
      transcriptItems.value.filter(
        (item) => item.status === 'pending' || item.status === 'transcribing',
      ).length,
  )

  const sortedTranscriptItems = computed(() =>
    [...transcriptItems.value].sort((left, right) => left.startedAt - right.startedAt),
  )

  const visibleTranscriptItems = computed(() => {
    const visible: TranscriptItem[] = []

    for (const item of sortedTranscriptItems.value) {
      if (item.status === 'pending' || item.status === 'transcribing') break
      if (item.status === 'done' && typeof item.text === 'string' && item.text.trim()) {
        visible.push(item)
      }
    }

    return visible
  })
  const hasBlockedTranscriptItem = computed(() =>
    sortedTranscriptItems.value.some(
      (item) => item.status === 'pending' || item.status === 'transcribing',
    ),
  )
  const completedTranscriptItems = computed(() =>
    sortedTranscriptItems.value.filter(
      (item) => item.status === 'done' && typeof item.text === 'string' && item.text.trim(),
    ),
  )
  const suggestionsLines = computed(() => {
    if (suggestions.value.length > 0) return suggestions.value
    if (suggestionsError.value) return [suggestionsError.value]
    if (isLoadingSuggestions.value) return ['Generando sugerencias.']
    if (lastSuggestionAt.value > 0) return ['Sin sugerencias nuevas.']
    return ['Esperando contexto suficiente.']
  })
  const summaryLines = computed(() => {
    if (summary.value) return [summary.value]
    if (summaryError.value) return [summaryError.value]
    if (isLoadingSummary.value) return ['Generando resumen.']
    return ['Esperando contexto suficiente.']
  })

  const transcriptionLines = computed(() => {
    if (isAiDisabledForCall.value) return [AI_DISABLED_MESSAGE]

    if (visibleTranscriptItems.value.length > 0) {
      return visibleTranscriptItems.value.map((item) => {
        const label = getPartyLabel(item.party)
        return `${label}: ${item.text}`
      })
    }

    if (status.value === 'waiting_audio') return ['Esperando audio de la llamada.']
    if (status.value === 'listening') return ['Escuchando conversación.']
    if (status.value === 'transcribing') return ['Transcribiendo conversación.']
    if (status.value === 'error') return [errorMessage.value || 'Error en transcripción.']
    return ['Esperando llamada conectada.']
  })

  const updateStatusFromWork = () => {
    if (!isAutoTranscribing.value || !workspace.isIaEnabled) return
    status.value = pendingChunks.value > 0 ? 'transcribing' : 'listening'
  }

  const clearInsightTimers = () => {
    if (suggestionsTimer !== null) {
      window.clearTimeout(suggestionsTimer)
      suggestionsTimer = null
    }
    if (summaryTimer !== null) {
      window.clearTimeout(summaryTimer)
      summaryTimer = null
    }
  }

  const resetCallAiData = () => {
    transcriptItems.value = []
    if (DEBUG_AUDIO_CHUNKS_ENABLED) {
      debugAudioChunks.value = []
    }
    suggestions.value = []
    summary.value = null
    suggestionsError.value = null
    summaryError.value = null
    lastSuggestedTranscriptIndex.value = 0
    lastSummarizedTranscriptIndex.value = 0
    lastSuggestionAt.value = 0
    lastSummaryAt.value = 0
    callStartedAt.value = null
    errorMessage.value = null
    activeSpeakers.value = { agent: false, customer: false }
    clearInsightTimers()
  }

  const beginCallSession = (callKey: string) => {
    if (currentCallKey === callKey) return

    const activeRecorders = [...recorders.values()]
    recorders.clear()
    void Promise.allSettled(activeRecorders.map((recorder) => recorder.stop()))
    isAutoTranscribing.value = false
    isAiDisabledForCall.value = false
    currentCallKey = callKey
    currentUsageSessionId = crypto.randomUUID()
    currentUsageGroupId = null
    currentUsageGroupRequest = null
    status.value = 'idle'
    resetCallAiData()
  }

  const endCallSession = () => {
    currentCallKey = null
    currentUsageSessionId = null
    currentUsageGroupId = null
    currentUsageGroupRequest = null
  }

  const toRealtimeTranscript = (items: TranscriptItem[]): RealtimeTranscriptItem[] =>
    items
      .filter((item) => item.status === 'done' && typeof item.text === 'string' && item.text.trim())
      .map((item) => ({
        speaker: item.party,
        text: item.text?.trim() || '',
      }))

  const scheduleSuggestions = (delayMs = INSIGHT_DEBOUNCE_MS) => {
    if (!isSuggestionsGenerationEnabled.value) return
    if (suggestionsTimer !== null) {
      window.clearTimeout(suggestionsTimer)
    }
    suggestionsTimer = window.setTimeout(() => {
      suggestionsTimer = null
      void maybeGenerateSuggestions()
    }, delayMs)
  }

  const scheduleSummary = (delayMs = INSIGHT_DEBOUNCE_MS) => {
    if (!isSummaryGenerationEnabled.value) return
    if (summaryTimer !== null) {
      window.clearTimeout(summaryTimer)
    }
    summaryTimer = window.setTimeout(() => {
      summaryTimer = null
      void maybeGenerateSummary()
    }, delayMs)
  }

  const scheduleRealtimeInsights = () => {
    if (isAiDisabledForCall.value || !workspace.isIaEnabled) return
    if (completedTranscriptItems.value.length === 0) return

    const now = Date.now()
    const completedCount = completedTranscriptItems.value.length
    const newItemsSinceSuggestion = completedTranscriptItems.value.slice(
      lastSuggestedTranscriptIndex.value,
    )
    const newCustomerLines = newItemsSinceSuggestion.filter(
      (item) => item.party === 'customer',
    ).length

    if (
      isSuggestionsGenerationEnabled.value &&
      !isLoadingSuggestions.value &&
      completedCount - lastSuggestedTranscriptIndex.value >= SUGGESTION_MIN_NEW_LINES &&
      newCustomerLines >= SUGGESTION_MIN_CUSTOMER_LINES
    ) {
      scheduleSuggestions(
        Math.max(INSIGHT_DEBOUNCE_MS, SUGGESTION_COOLDOWN_MS - (now - lastSuggestionAt.value)),
      )
    }

    if (!isSummaryGenerationEnabled.value || isLoadingSummary.value) return

    const hasSummary = Boolean(summary.value)
    const newSummaryLines = completedCount - lastSummarizedTranscriptIndex.value
    const callAgeMs = callStartedAt.value ? now - callStartedAt.value : 0
    const shouldCreateFirstSummary =
      !hasSummary &&
      (completedCount >= SUMMARY_FIRST_MIN_LINES || callAgeMs >= SUMMARY_FIRST_MIN_MS)
    const shouldUpdateSummary =
      hasSummary &&
      newSummaryLines >= SUMMARY_NEW_LINES &&
      now - lastSummaryAt.value >= SUMMARY_COOLDOWN_MS

    if (shouldCreateFirstSummary || shouldUpdateSummary) {
      scheduleSummary()
    }
  }

  const maybeGenerateSuggestions = async () => {
    if (!isSuggestionsGenerationEnabled.value) return
    if (isAiDisabledForCall.value || !workspace.isIaEnabled) return
    if (isLoadingSuggestions.value) return

    const completedItems = completedTranscriptItems.value
    const completedCount = completedItems.length
    const newItems = completedItems.slice(lastSuggestedTranscriptIndex.value)
    const newCustomerLines = newItems.filter((item) => item.party === 'customer').length
    const cooldownRemaining = SUGGESTION_COOLDOWN_MS - (Date.now() - lastSuggestionAt.value)

    if (
      completedCount - lastSuggestedTranscriptIndex.value < SUGGESTION_MIN_NEW_LINES ||
      newCustomerLines < SUGGESTION_MIN_CUSTOMER_LINES
    ) {
      return
    }

    if (cooldownRemaining > 0) {
      scheduleSuggestions(cooldownRemaining)
      return
    }

    isLoadingSuggestions.value = true
    suggestionsError.value = null
    lastSuggestedTranscriptIndex.value = completedCount

    try {
      const { generateRealtimeSuggestions } = useCallSttService()
      const usageGroupId = await ensureRealtimeUsageGroup()
      const payload = toRealtimeTranscript(completedItems.slice(-SUGGESTION_CONTEXT_LINES))
      const response = await generateRealtimeSuggestions({
        campaign_id: activeCampaignId(),
        transcript: payload,
        current_summary: summary.value,
        usage_group_id: usageGroupId,
      })
      suggestions.value = response.suggestions
      lastSuggestionAt.value = Date.now()
    } catch (error) {
      if (isAiDisabledError(error)) {
        handleAiDisabled()
        return
      }
      suggestionsError.value = 'No se pudieron generar sugerencias en este momento.'
    } finally {
      isLoadingSuggestions.value = false
    }
  }

  const maybeGenerateSummary = async (final = false) => {
    if (!isSummaryGenerationEnabled.value) return
    if (isAiDisabledForCall.value || !workspace.isIaEnabled) return
    if (isLoadingSummary.value) return

    const completedItems = completedTranscriptItems.value
    if (completedItems.length === 0) return

    const completedCount = completedItems.length
    const now = Date.now()
    const hasSummary = Boolean(summary.value)
    const shouldCreateFirstSummary =
      !hasSummary &&
      (completedCount >= SUMMARY_FIRST_MIN_LINES ||
        (callStartedAt.value ? now - callStartedAt.value >= SUMMARY_FIRST_MIN_MS : false))
    const shouldUpdateSummary =
      hasSummary &&
      completedCount - lastSummarizedTranscriptIndex.value >= SUMMARY_NEW_LINES &&
      now - lastSummaryAt.value >= SUMMARY_COOLDOWN_MS

    if (!final && !shouldCreateFirstSummary && !shouldUpdateSummary) return

    isLoadingSummary.value = true
    summaryError.value = null

    try {
      const { generateRealtimeSummary } = useCallSttService()
      const usageGroupId = await ensureRealtimeUsageGroup()
      const startIndex = summary.value ? lastSummarizedTranscriptIndex.value : 0
      const payload = toRealtimeTranscript(completedItems.slice(startIndex))
      if (payload.length === 0) return

      const response = await generateRealtimeSummary({
        campaign_id: activeCampaignId(),
        transcript: payload,
        previous_summary: summary.value,
        final,
        usage_group_id: usageGroupId,
      })
      summary.value = response.summary
      lastSummaryAt.value = Date.now()
      lastSummarizedTranscriptIndex.value = completedCount
    } catch (error) {
      if (isAiDisabledError(error)) {
        handleAiDisabled()
        return
      }
      summaryError.value = 'No se pudo generar el resumen en este momento.'
    } finally {
      isLoadingSummary.value = false
    }
  }

  const reserveTranscriptItem = ({
    id,
    party,
    startedAt,
  }: {
    id: string
    party: AudioParty
    startedAt: number
  }) => {
    if (isAiDisabledForCall.value || !workspace.isIaEnabled) return

    activeSpeakers.value[party] = true

    if (transcriptItems.value.some((item) => item.id === id)) return

    transcriptItems.value.push({
      id,
      party,
      startedAt,
      endedAt: null,
      status: 'pending',
      text: null,
      errorMessage: null,
    })
    updateStatusFromWork()
  }

  const markTranscriptItemSkipped = (id: string) => {
    const item = transcriptItems.value.find((entry) => entry.id === id)
    if (!item) return

    item.status = 'skipped'
    activeSpeakers.value[item.party] = false
    updateStatusFromWork()
  }

  const upsertDebugAudioChunk = (chunk: DebugAudioChunk) => {
    if (!DEBUG_AUDIO_CHUNKS_ENABLED) return

    const index = debugAudioChunks.value.findIndex((item) => item.id === chunk.id)
    if (index >= 0) {
      debugAudioChunks.value[index] = chunk
      return
    }
    debugAudioChunks.value.push(chunk)
  }

  const transcribeSegment = async (segment: VoiceActivitySegment) => {
    const item = transcriptItems.value.find((entry) => entry.id === segment.id)
    if (!item) return

    if (isAiDisabledForCall.value || !workspace.isIaEnabled) {
      item.status = 'skipped'
      item.text = null
      item.errorMessage = null
      activeSpeakers.value[segment.party] = false
      updateStatusFromWork()
      return
    }

    item.endedAt = segment.endedAt
    item.status = 'transcribing'
    activeSpeakers.value[segment.party] = false
    updateStatusFromWork()

    const debugChunkBase: Omit<DebugAudioChunk, 'status' | 'text' | 'errorMessage'> | null =
      DEBUG_AUDIO_CHUNKS_ENABLED
        ? {
            id: segment.id,
            party: segment.party,
            startedAt: segment.startedAt,
            endedAt: segment.endedAt,
            durationMs: segment.endedAt - segment.startedAt,
            sizeBytes: segment.wavBlob.size,
            wavBlob: segment.wavBlob,
          }
        : null

    if (debugChunkBase) {
      upsertDebugAudioChunk({
        ...debugChunkBase,
        status: 'transcribing',
        text: null,
        errorMessage: null,
      })
    }

    try {
      const { transcribeChunk } = useCallSttService()
      const usageGroupId = await ensureRealtimeUsageGroup()
      const file = new File(
        [segment.wavBlob],
        `webphone-${segment.party}-${segment.startedAt}.wav`,
        {
          type: 'audio/wav',
        },
      )
      const response = await withTimeout(
        transcribeChunk(file, activeCampaignId(), usageGroupId),
        TRANSCRIPTION_TIMEOUT_MS,
        'Tiempo de transcripción agotado.',
      )
      const segmentText = response.transcription_segments
        .map((transcriptionSegment) => transcriptionSegment.text.trim())
        .filter(Boolean)
        .join(' ')
      const text = segmentText || response.transcription_text?.trim() || ''

      lastProvider.value = response.provider
      lastModel.value = response.model

      if (!text) {
        item.status = 'skipped'
        item.text = null
        if (debugChunkBase) {
          upsertDebugAudioChunk({
            ...debugChunkBase,
            status: 'skipped',
            text: null,
            errorMessage: null,
          })
        }
        return
      }

      item.status = 'done'
      item.text = text
      item.errorMessage = null
      if (debugChunkBase) {
        upsertDebugAudioChunk({
          ...debugChunkBase,
          status: 'done',
          text,
          errorMessage: null,
        })
      }
    } catch (error) {
      if (isAiDisabledError(error)) {
        handleAiDisabled()
        item.status = 'skipped'
        item.text = null
        item.errorMessage = null
        return
      }
      if (isAxiosError(error) && error.response?.status === 422) {
        item.status = 'skipped'
        item.text = null
        item.errorMessage = null
        if (debugChunkBase) {
          upsertDebugAudioChunk({
            ...debugChunkBase,
            status: 'skipped',
            text: null,
            errorMessage: null,
          })
        }
        return
      }

      item.status = 'error'
      item.text = null
      item.errorMessage =
        error instanceof Error ? error.message : `${getPartyLabel(segment.party)}: error STT`
      errorMessage.value = item.errorMessage
      status.value = 'error'
      if (debugChunkBase) {
        upsertDebugAudioChunk({
          ...debugChunkBase,
          status: 'error',
          text: null,
          errorMessage: item.errorMessage,
        })
      }
    } finally {
      updateStatusFromWork()
    }
  }

  const createRecorder = (party: AudioParty, stream: MediaStream) =>
    createVoiceActivityRecorder({
      party,
      stream,
      onSpeechStart: reserveTranscriptItem,
      onSpeechDiscarded: ({ id }) => markTranscriptItemSkipped(id),
      onSegment: (segment) => {
        void transcribeSegment(segment)
      },
      onError: (error) => {
        errorMessage.value = error.message
        status.value = 'error'
      },
    })

  const setMediaStreams = (streams: { localStream: MediaStream; remoteStream: MediaStream }) => {
    clearMediaListeners()

    localStream.value = streams.localStream
    remoteStream.value = streams.remoteStream

    addMediaListener(streams.localStream, 'addtrack')
    addMediaListener(streams.localStream, 'removetrack')
    addMediaListener(streams.remoteStream, 'addtrack')
    addMediaListener(streams.remoteStream, 'removetrack')

    for (const track of [
      ...streams.localStream.getAudioTracks(),
      ...streams.remoteStream.getAudioTracks(),
    ]) {
      addMediaListener(track, 'ended', { once: true })
      addMediaListener(track, 'mute')
      addMediaListener(track, 'unmute')
    }

    refreshMediaState()

    if (!isAutoTranscribing.value) {
      status.value = hasLocalAudio.value || hasRemoteAudio.value ? 'waiting_audio' : 'idle'
    }

    if (
      workspace.isIaEnabled &&
      isRealtimeAiPanelEnabled.value &&
      !isAutoTranscribing.value
    ) {
      void startAutoTranscription()
    }
  }

  const startAutoTranscription = async () => {
    if (isAutoTranscribing.value) return
    if (!workspace.isIaEnabled) return
    if (!isRealtimeAiPanelEnabled.value) return
    isAiDisabledForCall.value = false
    suggestionsError.value = null
    summaryError.value = null
    errorMessage.value = null

    refreshMediaState()

    if (!localStream.value && !remoteStream.value) {
      status.value = 'waiting_audio'
      return
    }

    if (!callStartedAt.value) callStartedAt.value = Date.now()
    recorders.clear()

    if (localStream.value && hasLocalAudio.value) {
      recorders.set('agent', createRecorder('agent', localStream.value))
    }

    if (remoteStream.value && hasRemoteAudio.value) {
      recorders.set('customer', createRecorder('customer', remoteStream.value))
    }

    if (recorders.size === 0) {
      status.value = 'waiting_audio'
      return
    }

    isAutoTranscribing.value = true

    try {
      await Promise.all([...recorders.values()].map((recorder) => recorder.start()))
      status.value = 'listening'
      scheduleSummary(SUMMARY_FIRST_MIN_MS)
    } catch (error) {
      await Promise.allSettled([...recorders.values()].map((recorder) => recorder.stop()))
      isAutoTranscribing.value = false
      recorders.clear()
      errorMessage.value =
        error instanceof Error ? error.message : 'No se pudo iniciar la transcripción automática.'
      status.value = 'error'
    }
  }

  const stopAutoTranscription = async (generateFinalSummary = true) => {
    const activeRecorders = [...recorders.values()]
    recorders.clear()
    isAutoTranscribing.value = false
    activeSpeakers.value = { agent: false, customer: false }

    await Promise.all(activeRecorders.map((recorder) => recorder.stop()))
    clearInsightTimers()
    if (generateFinalSummary) {
      await maybeGenerateSummary(true)
    }

    if (status.value !== 'error') {
      status.value = 'stopped'
    }
  }

  watch(
    () =>
      completedTranscriptItems.value
        .map((item) => `${item.id}:${item.party}:${item.text}`)
        .join('|'),
    () => {
      scheduleRealtimeInsights()
    },
  )

  return {
    activeSpeakers,
    debugAudioChunks,
    errorMessage,
    hasLocalAudio,
    hasRemoteAudio,
    isAutoTranscribing,
    isLoadingSuggestions,
    setRealtimeAiPanelEnabled,
    setSuggestionsGenerationEnabled,
    setSummaryGenerationEnabled,
    isLoadingSummary,
    lastModel,
    lastProvider,
    localTrackCount,
    pendingChunks,
    remoteTrackCount,
    status,
    suggestions,
    suggestionsError,
    suggestionsLines,
    summary,
    summaryError,
    summaryLines,
    transcriptItems,
    transcriptionLines,
    hasBlockedTranscriptItem,
    visibleTranscriptItems,
    resetCallAiData,
    beginCallSession,
    endCallSession,
    setMediaStreams,
    startAutoTranscription,
    stopAutoTranscription,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCallAiStore, import.meta.hot))
}
